import LockIcon from "@mui/icons-material/Lock";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useState } from "react";
import {
  createIftaPaymentIntent,
  IftaGenerateRequest,
} from "../../api/reportsApi";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "",
);

// ── Card brand SVG logos ──────────────────────────────────────────────────────

const VisaLogo = () => (
  <svg
    width="38"
    height="24"
    viewBox="0 0 38 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="38" height="24" rx="4" fill="#1A1F71" />
    <text
      x="19"
      y="16"
      textAnchor="middle"
      fill="white"
      fontSize="11"
      fontWeight="bold"
      fontStyle="italic"
      fontFamily="Arial, sans-serif"
    >
      VISA
    </text>
  </svg>
);

const MastercardLogo = () => (
  <svg
    width="38"
    height="24"
    viewBox="0 0 38 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="38" height="24" rx="4" fill="#252525" />
    <circle cx="15" cy="12" r="7" fill="#EB001B" />
    <circle cx="23" cy="12" r="7" fill="#F79E1B" />
    <path d="M19 6.8a7 7 0 0 1 0 10.4A7 7 0 0 1 19 6.8z" fill="#FF5F00" />
  </svg>
);

const AmexLogo = () => (
  <svg
    width="38"
    height="24"
    viewBox="0 0 38 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="38" height="24" rx="4" fill="#2E77BC" />
    <text
      x="19"
      y="16"
      textAnchor="middle"
      fill="white"
      fontSize="8.5"
      fontWeight="bold"
      fontFamily="Arial, sans-serif"
      letterSpacing="0.5"
    >
      AMEX
    </text>
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatAmount = (amount: number, currency: string) => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount} ${currency.toUpperCase()}`;
  }
};

// ── Types ─────────────────────────────────────────────────────────────────────

interface IftaPaymentDialogProps {
  open: boolean;
  request: IftaGenerateRequest;
  amount: number;
  currency: string;
  unitAmount: number;
  truckCount: number;
  billableTruckCount: number;
  isFirstReport: boolean;
  subscribedTruckQuantity: number;
  token: string;
  onClose: () => void;
  onPaid: (paymentIntentId: string) => void;
}

// ── Payment form (needs to be inside <Elements>) ──────────────────────────────

const PaymentForm: React.FC<Omit<IftaPaymentDialogProps, "open">> = ({
  request,
  amount,
  currency,
  unitAmount,
  truckCount,
  billableTruckCount,
  isFirstReport,
  subscribedTruckQuantity,
  token,
  onClose,
  onPaid,
}) => {
  const theme = useTheme();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDark = theme.palette.mode === "dark";
  const formattedAmount = formatAmount(amount, currency);

  const cardElementOptions = {
    style: {
      base: {
        fontSize: "15px",
        color: isDark ? "#e2e8f0" : "#1e293b",
        fontFamily: theme.typography.fontFamily,
        "::placeholder": { color: isDark ? "#64748b" : "#94a3b8" },
        iconColor: isDark ? "#94a3b8" : "#64748b",
      },
      invalid: {
        color: theme.palette.error.main,
        iconColor: theme.palette.error.main,
      },
    },
  };

  const handlePay = async () => {
    setError(null);

    if (!stripe || !elements) {
      setError("Payment system is not ready. Please try again.");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError("Unable to find the card input.");
      return;
    }

    setProcessing(true);
    try {
      const intent = await createIftaPaymentIntent(request, token);

      const result = await stripe.confirmCardPayment(intent.clientSecret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        setError(result.error.message || "Payment failed. Please try again.");
        setProcessing(false);
        return;
      }

      if (result.paymentIntent?.status === "succeeded") {
        onPaid(result.paymentIntent.id);
        return;
      }

      setError("Payment was not completed. Please try again.");
      setProcessing(false);
    } catch (err: any) {
      setError(err?.message || "Payment failed. Please try again.");
      setProcessing(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isFirstReport ? (
            <>
              Your first IFTA report includes{" "}
              <strong>{subscribedTruckQuantity} truck(s)</strong> at no charge.
              This file lists <strong>{truckCount}</strong> — you are paying
              only for the <strong>{billableTruckCount}</strong> additional
              truck{billableTruckCount === 1 ? "" : "s"} in{" "}
              <strong>{request.quarter}</strong>.
            </>
          ) : (
            <>
              Generating the report for <strong>{request.quarter}</strong> is
              billed at {formatAmount(unitAmount, currency)} per truck.
            </>
          )}
        </Typography>

        {/* Price breakdown */}
        <Box
          sx={{
            mb: 2,
            borderRadius: 1,
            bgcolor: "action.hover",
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Stack divider={<Divider />}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                px: 2,
                py: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Trucks in report
              </Typography>
              <Typography variant="body2">{truckCount}</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                px: 2,
                py: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {isFirstReport
                  ? "Additional billable trucks"
                  : "Billable trucks"}{" "}
                × {formatAmount(unitAmount, currency)}
              </Typography>
              <Typography variant="body2">
                {billableTruckCount} × {formatAmount(unitAmount, currency)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                bgcolor: "action.selected",
              }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Total
              </Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                {formattedAmount}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Card input */}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {/* Header bar */}
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: "action.hover",
              borderBottom: "1px solid",
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <LockIcon sx={{ fontSize: 14, color: "success.main" }} />
              <Typography
                variant="caption"
                color="success.main"
                fontWeight={600}
              >
                Secure card payment
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
              <VisaLogo />
              <MastercardLogo />
              <AmexLogo />
            </Box>
          </Box>

          {/* Card element */}
          <Box
            sx={{
              px: 2,
              py: 1.75,
              bgcolor: "background.paper",
            }}
          >
            <CardElement options={cardElementOptions} />
          </Box>
        </Box>

        {/* Security note */}
        <Box
          sx={{ mt: 1.25, display: "flex", alignItems: "flex-start", gap: 0.5 }}
        >
          <LockIcon
            sx={{
              fontSize: 12,
              color: "text.disabled",
              mt: "2px",
              flexShrink: 0,
            }}
          />
          <Typography variant="caption" color="text.disabled" lineHeight={1.4}>
            Your card details are encrypted and sent directly to Stripe. We
            never see or store your card information. If generation fails after
            payment, your credit is kept — retry at no extra charge or contact{" "}
            <a href="mailto:support@adut.us" style={{ color: "inherit" }}>
              support@adut.us
            </a>
            .
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={processing} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handlePay}
          variant="contained"
          size="large"
          disabled={!stripe || processing}
          startIcon={
            processing ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <LockIcon />
            )
          }
          sx={{ minWidth: 160 }}
        >
          {processing ? "Processing…" : `Pay ${formattedAmount}`}
        </Button>
      </DialogActions>
    </>
  );
};

// ── Dialog wrapper ────────────────────────────────────────────────────────────

const IftaPaymentDialog: React.FC<IftaPaymentDialogProps> = ({
  open,
  onClose,
  ...rest
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const elementsOptions = {
    appearance: {
      theme: (isDark ? "night" : "stripe") as "night" | "stripe",
      variables: {
        colorPrimary: theme.palette.primary.main,
        colorBackground: theme.palette.background.paper,
        colorText: isDark ? "#e2e8f0" : "#1e293b",
        colorDanger: theme.palette.error.main,
        fontFamily: theme.typography.fontFamily,
        borderRadius: "4px",
      },
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pay for IFTA Report</DialogTitle>
      <Elements stripe={stripePromise} options={elementsOptions}>
        <PaymentForm onClose={onClose} {...rest} />
      </Elements>
    </Dialog>
  );
};

export default IftaPaymentDialog;
